import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import AuthApi from "../../api/authApi";
import moment from "moment-timezone";
import companyLogo from "../../assets/images/logo.png";
import dummyImage from "../../assets/images/uploadRetreat.png";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin } from "lucide-react";
import { resolveRetreatImageUrl } from "../../utils/processRetreatImage";
import { useAuthUser } from "../../hooks/useAuthUser";
import styles from "./UpcomingRetreats.module.scss";

const formatRetreatDates = (from: any, to: any) => {
  if (!from) return "";
  if (!to || from === to || moment(from).isSame(to, "day")) {
    return moment(from).tz("America/New_York").format("MMM D, YYYY");
  }
  if (moment(from).tz("America/New_York").isSame(moment(to).tz("America/New_York"), "month")) {
    return `${moment(from).tz("America/New_York").format("MMM D")} - ${moment(to)
      .tz("America/New_York")
      .format("D, YYYY")}`;
  }
  return `${moment(from).tz("America/New_York").format("MMM D")} - ${moment(to)
    .tz("America/New_York")
    .format("MMM D, YYYY")}`;
};

const UpcomingRetreats: React.FC = () => {
  const [upcomingRetreats, setUpcomingRetreats] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const authApi = new AuthApi();
  const userData = useAuthUser();

  useEffect(() => {
    const fetchRetreats = async () => {
      setLoading(true);
      try {
        const response: any = await authApi.getAllIncommingRetreats();
        if (response.success) {
          const currentDate = new Date();
          const upcoming = response.data.filter((retreat: any) => {
            const retreatDate = new Date(retreat.dateFrom);
            return retreatDate >= currentDate;
          });
          setUpcomingRetreats(upcoming);
        } else {
          toast.error("Failed to fetch upcoming retreats");
        }
      } catch (error) {
        toast.error("Error fetching retreats");
      }
      setLoading(false);
    };

    fetchRetreats();
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.hero}>
          {!userData && (
            <Link to="/dashboard">
              <img src={companyLogo} alt="LOVESTRONG Marriage" className={styles.logo} />
            </Link>
          )}
          <h1 className={styles.title}>Upcoming Retreats</h1>
          <p className={styles.subtitle}>
            Choose a retreat to view details and register.
          </p>
        </div>

        {loading ? (
          <div className={styles.status}>
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 mb-0">Loading retreats...</p>
          </div>
        ) : upcomingRetreats.length === 0 ? (
          <div className={styles.empty}>
            <CalendarDays size={28} color="#285d8f" />
            <p className="mb-0">No upcoming retreats available.</p>
          </div>
        ) : (
          <div className={styles.grid}>
            {upcomingRetreats.map((retreat) => {
              const retreatImage =
                resolveRetreatImageUrl(retreat.image1) || dummyImage;
              const language = retreat.language
                ? ` (${retreat.language[0].toUpperCase()}${retreat.language.slice(1)})`
                : "";
              const location = retreat.location
                ? `${retreat.location[0].toUpperCase()}${retreat.location.slice(1)}`
                : "";

              return (
                <a
                  key={retreat.id}
                  className={styles.card}
                  href={`/registration-retreat/${retreat.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className={styles.imageWrap}>
                    <img
                      src={retreatImage}
                      alt={retreat?.parishName || "Retreat"}
                      className={styles.image}
                    />
                  </div>
                  <div className={styles.body}>
                    <h2 className={styles.parish}>
                      {retreat?.parishName}
                      {language}
                    </h2>
                    {location ? (
                      <p className={styles.meta}>
                        <MapPin size={15} />
                        {location}
                      </p>
                    ) : null}
                    <p className={styles.meta}>
                      <CalendarDays size={15} />
                      {formatRetreatDates(retreat.dateFrom, retreat.dateTo)}
                    </p>
                    <span className={styles.cta}>
                      View & register
                      <ArrowRight size={16} />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingRetreats;
